import { RegisterCredentials, UserCredentials } from "src/types";
import { httpProxy } from "../api";
import { createUserWithEmailAndPassword, getAuth, sendEmailVerification, sendPasswordResetEmail, signInWithEmailAndPassword, updateProfile, User } from "firebase/auth";
import { FirebaseError } from "firebase/app";
import { APIResponse } from "../types";
import { CustomerEnvelope, UserProfileEnvelope, UserSubscriptionEnvelope } from "./types";
import { AxiosError } from "axios";

async function login(credentials: UserCredentials): Promise<APIResponse<User>> {
  try {
    const auth = getAuth();
    const userRegistered = await signInWithEmailAndPassword(auth, credentials.email, credentials.password);

    return {
      status: 200,
      content: userRegistered.user,
      success: true
    };
    
  } catch (error: unknown) {
    if (error instanceof FirebaseError) {
      return {
        status: 400,
        errorCode: error.code,
        success: false
      };
    }

    return {
      status: 500,
      success: false,
    };
  }
}

async function subscribeEmail(email: string) {
  return await httpProxy.post("/public/subscribers", { email });
}

async function resetPassword(email: string) {

  try {
    const auth = getAuth();
    
    // Send the password reset email asynchronously
    await sendPasswordResetEmail(auth, email);
    
    // If successful, return a status 200 response with a success message
    return {
      status: 200,
      message: 'Password reset email sent'
    };
    
  } catch (error: any) {
    console.error('Error sending password reset email:', error);
    
    // Return an error response with status 500 and the error message
    return {
      status: 500,
      message: `Error sending password reset email: ${error.message}`
    };
  }
  //return await httpInternal.get("reset-password?email=" + email);
}

async function register(credentials: RegisterCredentials) {
  try {
    const auth = getAuth();
    
    // Note: Firebase user is created first, then backend is called
    // Backend verifies reCAPTCHA token (recaptchaToken is included in credentials)
    // If backend rejects due to reCAPTCHA failure, Firebase user will still exist (orphaned)
    const userRegistered = await createUserWithEmailAndPassword(auth, credentials.email, credentials.password);

    await updateProfile(userRegistered.user, { displayName: credentials.name })
    await sendEmailVerification(userRegistered.user)

    // create user in the database - backend will verify reCAPTCHA token here
    // recaptchaToken is included in credentials and sent to backend for verification
    await httpProxy.post("/users", {
      ...credentials,
      firebaseUserUuid: userRegistered.user.uid
    });

    return {
      status: 200,
      user: userRegistered,
      message: 'Password reset email sent'
    };
    
  } catch (error: unknown) {
    if (error instanceof FirebaseError) {
      return {
        status: 400,
        code: error.code,
        message: error.message
      };
    }

    // Handle Axios errors (backend API errors, including reCAPTCHA errors)
    if (error instanceof AxiosError) {
      const status = error.response?.status || 500;
      const message = error.response?.data?.message || error.message || 'Unknown error';
      
      return {
        status,
        message,
        code: error.response?.data?.code,
      };
    }

    return {
      status: 500,
      message: `Error creating new user: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

async function registerGoogleUser(userData: { name: string; email: string; firebaseUserUuid: string; preferredLanguage?: string }) {
  try {
    // Create user in the database - Google users don't need password or email verification
    const response = await httpProxy.post("/users", {
      name: userData.name,
      email: userData.email,
      firebaseUserUuid: userData.firebaseUserUuid,
      isGoogleUser: true, // Flag to indicate this is a Google user
      preferredLanguage: userData.preferredLanguage || 'en' // Include preferred language
    });

    return {
      status: response.status,
      data: response.data,
      message: 'Google user created successfully'
    };
    
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return {
        status: error.response?.status || 500,
        message: error.response?.data?.message || 'Error creating Google user'
      };
    }

    return {
      status: 500,
      message: `Error creating Google user: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

async function resendConfirmationEmail() {
  try {
    const auth = getAuth();
    
    // Send the password reset email asynchronously
    const user = auth.currentUser;
    if (!user) {
      return {
        status: 404
      };
    }

    await sendEmailVerification(user)

    return {
      status: 200
    };
    
  } catch (error: unknown) {
    if (error instanceof FirebaseError) {
      return {
        status: 400,
        code: error.code,
        message: error.message
      };
    }

    return {
      status: 500,
      message: `Error sending confirmation email`
    };
  }
  // return await httpInternal.get("confirmation-email?email=" + email);
}

async function needsConfirmation() {
  try {
    const auth = getAuth();
    
    // Send the password reset email asynchronously
    const hasUser = auth.currentUser;
    if (!hasUser) {
      // console.log('This should not happen');
      return false;
    }
    return !auth.currentUser.emailVerified;
  } catch (error: unknown) {
    throw new Error(`Error checking if user needs confirmation: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

async function linkUserToCustomer(stripeSessionId: string): Promise<UserProfileEnvelope> {
  try {
    const user = getAuth().currentUser;
    const firebaseUserUuid = user?.uid;
    
    // bind user to subscription
    const response = await httpProxy.post("/users/link-user-customer", { stripeSessionId, firebaseUserUuid });
    return {
      status: response.status,
      message: response.status === 200 ? 'User binded to subscription' : 'Error binding user to subscription',
      data: response.data
    };
  } catch (error: unknown) {
    return {
      status: 500,
      message: "Unknown error",
      data: null
    };
  }
}

async function checkSubscriptionStatus(firebaseUserUuid: string): Promise<UserSubscriptionEnvelope> {
    try {
      const response = await httpProxy.get(`/users/subscription/${firebaseUserUuid}`);
      return {
        status: response.status,
        message: 'Subscription status checked',
        data: response.data
      };
    } catch (error: unknown) {
      console.error('Error checking subscription status:', error);
      if (error instanceof AxiosError) {
        return {
          status: error.response?.status ?? 500,
          message: error.response?.data?.message ?? "Unknown Error",
          data: error.response?.data ?? null
        };
      }
      
      return {
        status: 500,
        message: "Unknown error",
        data: null
      };
    }
  }

async function getUserByFirebaseUuid(firebaseUserUuid: string): Promise<UserProfileEnvelope> {
  try {
    const response = await httpProxy.get(`/users/firebase/${firebaseUserUuid}`);

    return {
      status: response.status,
      message: response.status === 200 ? 'User profile found' : 'We could not find user profile',
      data: response.data
    };
  } catch (error: unknown) {
    console.error('Error getting user by firebase uuid:', error);
    return {
      status: 500,
      message: "Unknown error",
      data: null
    };
  }
}

/**
 * Get the current user's profile based on auth token.
 * During impersonation, the backend middleware swaps the identity,
 * so this returns the impersonated user's profile automatically.
 */
async function getCurrentUser(): Promise<UserProfileEnvelope> {
  try {
    const response = await httpProxy.get('/users/me');

    return {
      status: response.status,
      message: response.status === 200 ? 'User profile found' : 'We could not find user profile',
      data: response.data
    };
  } catch (error: unknown) {
    console.error('Error getting current user:', error);
    return {
      status: 500,
      message: "Unknown error",
      data: null
    };
  }
}

// async function updateProfile(updatedProfile: EditProfile): Promise<APIResponse<UserProfile>> {
//   try {
//     const user = getAuth().currentUser;
//     const firebaseUserUuid = user?.uid;
//     // Send updated profile data to the backend to persist changes
//     const response = await httpProxy.put(`/users/firebase/${firebaseUserUuid}`, updatedProfile);

//     return {
//       status: response.status,
//       content: response.data,
//       success: response.status === 200,
//     };
//   } catch (error: unknown) {
//     console.error("Error updating user profile:", error);
//     return {
//       status: 500,
//       success: false,
//     };
//   }
// }

async function updateName(firebaseUserUuid: string, name: string) {
    try {
        // Send updated profile data to the backend to persist changes
        const response = await httpProxy.put(`/users/firebase/${firebaseUserUuid}/name`, { name });
        return {
            status: response.status,
            content: response.data,
            success: response.status === 200,
        };
      } catch (error: unknown) {
        console.error("Error updating user profile name:", error);
        return {
          status: 500,
          success: false,
        };
      }
}

async function updateEmail(firebaseUserUuid: string, email: string) {
    try {
        // Send updated profile data to the backend to persist changes
        const response = await httpProxy.put(`/users/firebase/${firebaseUserUuid}/email`, { email });
        return {
            status: response.status,
            content: response.data,
            success: response.status === 200,
        };
      } catch (error: unknown) {
        console.error("Error updating user profile name:", error);
        return {
          status: 500,
          success: false,
        };
      }
}

async function updatePreferredLanguage(firebaseUserUuid: string, preferredLanguage: string) {
    try {
        const url = `/users/firebase/${firebaseUserUuid}/preferred-language`;
        const body = { preferredLanguage };
        
        // Send updated preferred language to the backend to persist changes
        const response = await httpProxy.put(url, body);
        
        console.log('[UserService] Preferred language update response:', {
          status: response.status,
          data: response.data
        });
        
        return {
            status: response.status,
            content: response.data,
            success: response.status === 200,
        };
      } catch (error: unknown) {
        // Handle Axios errors
        if (error instanceof AxiosError) {
          const status = error.response?.status || 500;
          
          console.error('[UserService] Error updating preferred language:', {
            status,
            url: error.config?.url,
            fullUrl: error.config ? `${error.config.baseURL}${error.config.url}` : 'unknown',
            method: error.config?.method,
            requestData: error.config?.data,
            responseData: error.response?.data,
            message: error.message
          });
          
          // If endpoint doesn't exist (404), treat as success but log warning
          // This allows the app to continue working even if backend endpoint is not implemented yet
          if (status === 404) {
            console.warn(`[UserService] Preferred language endpoint not found (404). Language updated locally only.`);
            return {
              status: 200, // Return success to allow frontend to continue
              success: true,
              message: 'Language updated locally (backend endpoint not available)'
            };
          }
          
          const message = error.response?.data?.message || error.message || 'Error updating preferred language';
          
          return {
            status,
            message,
            success: false,
          };
        }
        
        return {
          status: 500,
          success: false,
          message: `Error updating preferred language: ${error instanceof Error ? error.message : 'Unknown error'}`
        };
      }
}

async function submitFeedback(data: { message: string; brandId?: number | null }) {
  const response = await httpProxy.post("/users/free-access-feedback", JSON.stringify(data));
  return response;
}

async function getCustomer(firebaseUserUuid: string): Promise<CustomerEnvelope>  {
    try {
        const response = await httpProxy.get(`/users/customer/${firebaseUserUuid}`);
        return {
            status: response.status,
            data: response.data,
            message: response.status === 200 ? 'customer found' : 'error retrieving customer'
        };
      } catch (error: unknown) {
        console.error("Error getting customer by uuid", error);
        return {
            status: 500,
            data: null,
            message: 'error retrieving customer'
        };
    }
}

async function getAllUsers() {
    try {
        const response = await httpProxy.get('/users');
        return {
            status: response.status,
            data: response.data,
            message: 'Users retrieved successfully'
        };
    } catch (error: unknown) {
        console.error('Error getting all users:', error);
        // TODO: Implement the backend endpoint GET /users if it does not exist
        if (error instanceof AxiosError) {
             return {
                status: error.response?.status || 500,
                message: error.response?.data?.message || 'Error retrieving users',
                data: []
             };
        }
        return {
            status: 500,
            message: 'Unknown error retrieving users',
            data: []
        };
    }
}

export default {
  login,
  subscribeEmail,
  resetPassword,
  register,
  registerGoogleUser,
  resendConfirmationEmail,
  needsConfirmation,
  linkUserToCustomer,
  checkSubscriptionStatus,
  getUserByFirebaseUuid,
  submitFeedback,
  getCustomer,
  updateName,
  updateEmail,
  updatePreferredLanguage,
  getAllUsers,
  getCurrentUser
};
